import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import PropTypes from 'prop-types';
import styles from './ImportSavedListModalContent.module.scss';
import { AlloyButton, ChildModal, Toggle, useModal } from 'cat-ecommerce-alloy';
import { STATUS } from '@app/constants/commonConstants';
import { loadSavedListDetails } from '@app/store/account/actions';
import SavedListContainer from '../SavedListContainer';
import { normalizeUrl, replaceTokensInUrl } from '@app/utils';
import endpoints from '../../../../constants/endpoints';
import { defaultSearchOptions } from './utils';
import CreateListForm from '@app/components/saved-lists/CreateListForm';

const DEFAULT_DISPLAYED_LISTS = 6;

const ImportSavedListModalContent = ({ close, isOpen }) => {
  const [t] = useTranslation();
  const childModalOptions = useModal({
    isChild: true,
    parentIsOpen: isOpen
  });

  const savedLists = useSelector(s => s.account?.savedLists) ?? [];
  const savedListsStatus = useSelector(s => s.account?.savedListsStatus);
  const isErrorLoadingSavedLists = savedListsStatus === STATUS.REJECTED;
  const isLoadingLoadingSavedLists = savedListsStatus === STATUS.PENDING;
  const dispatch = useDispatch();

  const customerType = useSelector(
    s => s.shoppingPreferences?.shoppingPreferencesData.customerType
  );
  const isCSPCustomer = useSelector(s => s.common?.isCSPCustomer);
  const isNotIAUser = customerType && customerType !== 'IA';

  const [savedListsPerPage, setSavedListsPerPage] = useState(
    DEFAULT_DISPLAYED_LISTS
  );
  const [showOnlyMyLists, setShowOnlyMyLists] = useState(true);
  const handleLoadMoreLists = () =>
    setSavedListsPerPage(savedListsPerPage + DEFAULT_DISPLAYED_LISTS);

  const showOnlyListsText = t('SHOW_ONLY_MY_LISTS');

  const { SAVED_LISTS_ACCOUNT_SECTION } = endpoints;
  const { storeId, langId, catalogId } = useSelector(state => state.common);
  const savedListsLink = replaceTokensInUrl(
    SAVED_LISTS_ACCOUNT_SECTION,
    storeId,
    catalogId,
    langId
  );

  const filteredLists = savedLists.filter(
    (_, index) => index < savedListsPerPage
  );

  const shouldShowLoadMore =
    !isErrorLoadingSavedLists &&
    !isLoadingLoadingSavedLists &&
    savedLists.length > DEFAULT_DISPLAYED_LISTS;

  const shouldShowManageListsBtn =
    !isErrorLoadingSavedLists &&
    !isLoadingLoadingSavedLists &&
    savedLists.length > 0;

  const shouldDisableLoadMore = savedLists.length <= savedListsPerPage;

  useEffect(() => {
    dispatch(loadSavedListDetails(defaultSearchOptions, showOnlyMyLists));
  }, [dispatch, showOnlyMyLists]);

  return (
    <>
      {(isNotIAUser || isCSPCustomer) && (
        <Toggle
          id="mapView"
          className={cx(styles['toggle-button'], 'ms-2')}
          label={showOnlyListsText}
          labelClassName="d-flex text-capitalize u-fs--16"
          placement="start"
          size="small"
          checked={showOnlyMyLists}
          setChecked={setShowOnlyMyLists}
        />
      )}
      <div className={cx(styles['scrollable-section'], 'me-3 overflow-y-auto')}>
        <SavedListContainer
          close={close}
          filteredLists={filteredLists}
          handleOpenChild={childModalOptions.open}
        />
      </div>
      {shouldShowManageListsBtn && (
        <div className="d-flex justify-content-center justify-content-lg-start my-3 my-lg-2">
          <a
            className={cx(
              'btn btn-ghost btn-lg btn-lg-lg w-sm-100 ps-lg-0',
              styles['manage-lists-btn']
            )}
            href={normalizeUrl(savedListsLink)}
          >
            {t('MANAGE_LISTS')}
          </a>
        </div>
      )}
      <ChildModal
        {...childModalOptions}
        title={t('SL_CREATE_LIST')}
        backButtonMessage={t('BACK_TO_IMPORT_SAVED_LIST')}
      >
        <CreateListForm
          close={childModalOptions.close}
          createSavedListData={{ items: [] }}
        />
      </ChildModal>
      {shouldShowLoadMore && (
        <div className="d-flex justify-content-center">
          <AlloyButton
            buttonType="secondary"
            onClick={handleLoadMoreLists}
            disabled={shouldDisableLoadMore}
            className="btn-dynamic w-100 w-lg-auto"
          >
            {t('LOAD_MORE')}
          </AlloyButton>
        </div>
      )}
    </>
  );
};

export default ImportSavedListModalContent;

ImportSavedListModalContent.propTypes = {
  close: PropTypes.func,
  isOpen: PropTypes.bool
};
